import { devLog, devError } from '../utils/devLogger';
/**
 * Facilitator Learner Lookup
 * Search by name or ID number and show last login + latest document/template submissions.
 */
import React, { useMemo, useState } from 'react';
import { Card, Table, Form, Badge, Alert, Spinner, Button } from 'react-bootstrap';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

const formatTimestamp = (value) => {
  if (!value) return 'Never';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const resolveLastLogin = (learner) => {
  if (!learner) return null;
  return learner.lastLoginAt || learner.lastLogin || learner.firstLoginAt || learner.emailVerifiedAt || null;
};

const FacilitatorLearnerLookup = ({ learners }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [error, setError] = useState('');

  const filteredLearners = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return learners.filter((learner) => {
      const name = (learner.displayName || learner.name || '').toLowerCase();
      const email = (learner.email || '').toLowerCase();
      const idNumber = String(learner.idNumber || '').toLowerCase();
      return name.includes(term) || email.includes(term) || idNumber.includes(term);
    });
  }, [learners, searchTerm]);

  const loadLatestDocument = async (learnerId) => {
    try {
      const docsQuery = query(
        collection(db, 'documents'),
        where('userId', '==', learnerId),
        orderBy('uploadedAt', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(docsQuery);
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        return { id: docSnap.id, ...docSnap.data() };
      }
    } catch (err) {
      devLog('Primary document query failed, falling back:', err);
      const fallbackQuery = query(
        collection(db, 'documents'),
        where('userId', '==', learnerId)
      );
      const snapshot = await getDocs(fallbackQuery);
      const docs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      docs.sort((a, b) => {
        const aDate = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt || 0);
        const bDate = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt || 0);
        return bDate - aDate;
      });
      return docs[0] || null;
    }
    return null;
  };

  const loadLatestTemplateSubmission = async (learnerId) => {
    try {
      const submissionsQuery = query(
        collection(db, 'templateSubmissions'),
        where('learnerId', '==', learnerId),
        orderBy('submittedAt', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(submissionsQuery);
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        return { id: docSnap.id, ...docSnap.data() };
      }
    } catch (err) {
      devLog('Primary template query failed, falling back:', err);
      const fallbackQuery = query(
        collection(db, 'templateSubmissions'),
        where('learnerId', '==', learnerId)
      );
      const snapshot = await getDocs(fallbackQuery);
      const docs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      docs.sort((a, b) => {
        const aDate = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const bDate = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return bDate - aDate;
      });
      return docs[0] || null;
    }
    return null;
  };

  const handleSelectLearner = async (learner) => {
    setSelectedLearner(learner);
    setError('');
    setLoadingActivity(true);
    try {
      const [latestDocument, latestTemplate] = await Promise.all([
        loadLatestDocument(learner.id || learner.uid),
        loadLatestTemplateSubmission(learner.id || learner.uid)
      ]);
      setActivity({
        latestDocument,
        latestTemplate
      });
    } catch (err) {
      devError('Error loading learner activity:', err);
      setError('Failed to load learner activity.');
    } finally {
      setLoadingActivity(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <strong>Learner Lookup</strong>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Label>Search by name, email, or ID number</Form.Label>
          <Form.Control
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="e.g. Thabo Mthembu or 9001015009087"
          />
        </Form.Group>

        {searchTerm.trim() && filteredLearners.length === 0 && (
          <Alert variant="warning">No learners found for that search.</Alert>
        )}

        {filteredLearners.length > 0 && (
          <div className="table-responsive mb-3">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>ID Number</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLearners.map((learner) => (
                  <tr key={learner.id || learner.uid}>
                    <td>{learner.displayName || learner.name || 'N/A'}</td>
                    <td>{learner.email}</td>
                    <td>{learner.phoneNumber || learner.alternateNumber || 'N/A'}</td>
                    <td>{learner.idNumber || 'N/A'}</td>
                    <td>
                      <Button variant="outline-primary" size="sm" onClick={() => handleSelectLearner(learner)}>
                        View Activity
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {selectedLearner && (
          <Card className="mt-3">
            <Card.Header>
              Activity for {selectedLearner.displayName || selectedLearner.name || selectedLearner.email}
            </Card.Header>
            <Card.Body>
              {loadingActivity ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2">Loading activity...</p>
                </div>
              ) : (
                <>
                  {(() => {
                    const lastLoginValue = resolveLastLogin(selectedLearner);
                    const hasActivity = Boolean(activity?.latestDocument || activity?.latestTemplate);
                    const label = lastLoginValue
                      ? formatTimestamp(lastLoginValue)
                      : hasActivity
                        ? 'Unknown (activity exists)'
                        : 'Never';
                    const badgeVariant = lastLoginValue
                      ? 'success'
                      : hasActivity
                        ? 'warning'
                        : 'secondary';
                    return (
                      <p>
                        <strong>Last Login:</strong>{' '}
                        <Badge bg={badgeVariant}>{label}</Badge>
                      </p>
                    );
                  })()}
                  <p>
                    <strong>Phone:</strong>{' '}
                    {selectedLearner.phoneNumber || selectedLearner.alternateNumber || 'N/A'}
                  </p>
                  <p>
                    <strong>Latest Document Submission:</strong>{' '}
                    {activity?.latestDocument ? (
                      <>
                        {activity.latestDocument.category || 'Document'} —{' '}
                        {formatTimestamp(activity.latestDocument.uploadedAt)}
                      </>
                    ) : (
                      'None'
                    )}
                  </p>
                  <p>
                    <strong>Latest Fillable Submission:</strong>{' '}
                    {activity?.latestTemplate ? (
                      <>
                        {activity.latestTemplate.templateTitle || 'Template'} —{' '}
                        {formatTimestamp(activity.latestTemplate.submittedAt)}
                      </>
                    ) : (
                      'None'
                    )}
                  </p>
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Card.Body>
    </Card>
  );
};

export default FacilitatorLearnerLookup;
